import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Ruler, ShieldCheck, Wrench, Clock } from "lucide-react";
import { FadeIn, MaskedLine, Crosshair, MonoLabel } from "../components/motion";
import EditorialMarquee from "../components/Marquee";

const HERO_IMG = "https://images.unsplash.com/photo-1579847188804-ecba0e2ea330?q=80&w=2400&auto=format&fit=crop";
const INTRO_IMG = "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?q=80&w=1600&auto=format&fit=crop";
const GEOTEXTILE_IMG = "/products/geocloth-home.jpg";
const GEOGRID_IMG = "/products/geogrid-home.jpg";
const QUARRY_IMG = "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?q=80&w=1200&auto=format&fit=crop";
const ROADING_IMG = "https://images.unsplash.com/photo-1652303518314-e1196ef221f6?q=80&w=1000&auto=format&fit=crop";
const SITE_IMG = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1000&auto=format&fit=crop";
const ENGINEER_IMG = "https://images.unsplash.com/photo-1581094794329-c8112a89af12?q=80&w=1000&auto=format&fit=crop";

const FEATURES = [
    { n: "01", icon: Ruler, title: "Technical Knowledge", text: "Solutions backed by practical understanding of ground engineering and construction requirements." },
    { n: "02", icon: ShieldCheck, title: "Quality Products", text: "Reliable geotextile and geogrid products selected for demanding applications." },
    { n: "03", icon: Wrench, title: "Practical Solutions", text: "We focus on finding products that work for the actual conditions and requirements of your project." },
    { n: "04", icon: Clock, title: "Reliable Service", text: "Straightforward advice, responsive service and dependable product supply." },
];

const CATEGORIES = [
    { slug: "geocloth", title: "Geocloth", text: "Separation, filtration, drainage, protection and stabilisation.", img: GEOTEXTILE_IMG },
    { slug: "geogrid", title: "Geogrid", text: "Reinforcement, load distribution, pavement support and ground stabilisation.", img: GEOGRID_IMG },
    { slug: "composite", title: "Composite Geocloth + Geogrid", text: "Separation and reinforcement combined in a single engineered layer.", img: "/products/composite-home.jpg" },
];

const APPLICATIONS = [
    { label: "Roading", img: ROADING_IMG },
    { label: "Civil Construction", img: SITE_IMG },
    { label: "Earthworks", img: QUARRY_IMG },
    { label: "Drainage", img: ENGINEER_IMG },
    { label: "Ground Stabilisation", img: INTRO_IMG },
    { label: "Infrastructure", img: HERO_IMG },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    return (
        <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="home-hero">
            <motion.div className="absolute inset-0" style={{ y, scale }}>
                <img src={HERO_IMG} alt="Civil construction site at dusk" className="w-full h-full object-cover" />
            </motion.div>
            <div className="absolute inset-0 bg-black/65" />
            <div className="absolute inset-0 border-x border-white/5 mx-4 md:mx-8 pointer-events-none" />
            <Crosshair className="top-24 left-4 md:left-8 -translate-x-1/2" />
            <Crosshair className="top-24 right-4 md:right-8 translate-x-1/2" />

            <div className="relative px-4 md:px-8 pb-10 md:pb-14 pt-40">
                <FadeIn delay={0.1}>
                    <MonoLabel data-testid="hero-kicker">Geosynthetics / Ground Engineering / Supply</MonoLabel>
                </FadeIn>
                <h1 className="mt-6 font-heading font-black uppercase tracking-tighter leading-[0.9] text-[13.5vw] md:text-[9.5vw]" data-testid="hero-headline">
                    <MaskedLine delay={0.15}>Engineered</MaskedLine>
                    <MaskedLine delay={0.27}>Solutions.</MaskedLine>
                    <MaskedLine delay={0.39} className="text-[#FF5C00]">Solid Ground.</MaskedLine>
                </h1>
                <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
                    <FadeIn delay={0.55} className="max-w-md">
                        <p className="text-base md:text-lg text-white/70 leading-relaxed" data-testid="hero-subtext">
                            Geotextiles, geogrids and ground engineering products designed to improve stability, performance and long-term project outcomes.
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.65} className="flex flex-col sm:flex-row gap-px bg-white/10 border border-white/10">
                        <Link
                            to="/products"
                            data-testid="hero-view-products-cta"
                            className="group flex items-center justify-between gap-6 bg-[#FF5C00] text-white px-8 h-14 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            View Products
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-talk-to-team-cta"
                            className="group flex items-center justify-between gap-6 bg-black/70 backdrop-blur text-white px-8 h-14 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            Talk to Our Team
                            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </Link>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
};

const Intro = () => (
    <section className="border-b border-line" data-testid="home-intro">
        <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative p-8 md:p-16 lg:p-24 border-b lg:border-b-0 lg:border-r border-line">
                <Crosshair className="top-4 left-4" />
                <FadeIn><MonoLabel>01 / Who We Are</MonoLabel></FadeIn>
                <h2 className="mt-8 font-heading font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl" data-testid="intro-headline">
                    <MaskedLine inView>Built for the</MaskedLine>
                    <MaskedLine inView delay={0.1}>Ground Beneath</MaskedLine>
                    <MaskedLine inView delay={0.2}>Your Project.</MaskedLine>
                </h2>
                <FadeIn delay={0.3} className="mt-10 max-w-lg">
                    <p className="text-white/70 leading-relaxed text-base md:text-lg">
                        Solid Ground Solutions supplies geotextiles, geogrids and engineered ground products for demanding civil, infrastructure and construction applications.
                    </p>
                    <p className="mt-6 text-white/70 leading-relaxed text-base md:text-lg">
                        No generic sales pitch. We match products to ground conditions, loads and design intent — so the ground under your project performs the way it was engineered to.
                    </p>
                </FadeIn>
                <FadeIn delay={0.4}>
                    <Link to="/about" data-testid="intro-about-link" className="group mt-10 inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-[#FF5C00] hover:text-white transition-colors duration-300">
                        More About Us
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </FadeIn>
            </div>
            <div className="relative min-h-[320px] lg:min-h-0 overflow-hidden group">
                <img src={INTRO_IMG} alt="Concrete infrastructure structure" className="absolute inset-0 w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/20" />
                <span className="absolute bottom-4 right-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/60">FIG. 01 — Structure</span>
            </div>
        </div>
    </section>
);

const Features = () => (
    <section className="border-b border-line" data-testid="home-features">
        <div className="p-8 md:p-16 lg:p-24 pb-0 md:pb-0 lg:pb-0">
            <FadeIn><MonoLabel>02 / Why Solid Ground Solutions</MonoLabel></FadeIn>
            <FadeIn delay={0.1}>
                <h2 className="mt-8 mb-12 md:mb-16 font-heading font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl max-w-4xl">
                    Ground Engineering, Done Properly.
                </h2>
            </FadeIn>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 border-t border-line">
            {FEATURES.map((f, i) => (
                <FadeIn key={f.n} delay={i * 0.08} className="group relative p-8 md:p-12 border-b md:border-b-0 xl:border-b-0 border-r border-line last:border-r-0 hover:bg-charcoal transition-colors duration-500">
                    <div className="flex items-start justify-between">
                        <f.icon className="w-6 h-6 text-[#FF5C00]" strokeWidth={1.5} />
                        <span className="font-mono text-xs text-white/30">{f.n}</span>
                    </div>
                    <h3 className="mt-16 md:mt-24 font-heading font-extrabold uppercase tracking-tight text-xl md:text-2xl" data-testid={`feature-title-${f.n}`}>
                        {f.title}
                    </h3>
                    <p className="mt-4 text-sm text-white/60 leading-relaxed">{f.text}</p>
                    <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#FF5C00] transition-[width] duration-500 group-hover:w-full" />
                </FadeIn>
            ))}
        </div>
    </section>
);

const Categories = () => (
    <section className="border-b border-line" data-testid="home-categories">
        <div className="p-8 md:p-16 lg:p-24 pb-12 md:pb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
                <FadeIn><MonoLabel>03 / Product Categories</MonoLabel></FadeIn>
                <FadeIn delay={0.1}>
                    <h2 className="mt-8 font-heading font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl">
                        What We Supply.
                    </h2>
                </FadeIn>
            </div>
            <FadeIn delay={0.2}>
                <Link to="/products" data-testid="categories-all-link" className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-white/70 hover:text-[#FF5C00] transition-colors duration-300">
                    Full Catalogue <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </FadeIn>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 border-t border-line">
            {CATEGORIES.map((c, i) => (
                <FadeIn key={c.slug} delay={i * 0.1} className="group relative border-b lg:border-b-0 lg:border-r border-line last:border-r-0">
                    <Link to={`/products?cat=${c.slug}`} data-testid={`category-${c.slug}-card`} className="block">
                        <div className="relative h-64 md:h-80 overflow-hidden bg-white">
                            <img src={c.img} alt={c.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700" />
                            <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-black text-white px-3 py-1">0{i + 1}</span>
                        </div>
                        <div className="p-8 md:p-10">
                            <h3 className="font-heading font-extrabold uppercase tracking-tight text-2xl md:text-3xl group-hover:text-[#FF5C00] transition-colors duration-300">{c.title}</h3>
                            <p className="mt-3 text-sm text-white/60 leading-relaxed">{c.text}</p>
                            <span className="mt-8 inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-[#FF5C00]">
                                View Products <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </span>
                        </div>
                    </Link>
                </FadeIn>
            ))}
        </div>
    </section>
);

const Applications = () => (
    <section className="border-b border-line" data-testid="home-applications">
        <div className="p-8 md:p-16 lg:p-24 pb-12 md:pb-16">
            <FadeIn><MonoLabel>04 / Featured Applications</MonoLabel></FadeIn>
            <FadeIn delay={0.1}>
                <h2 className="mt-8 font-heading font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl">
                    Where Our Products Work.
                </h2>
            </FadeIn>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 border-t border-line">
            {APPLICATIONS.map((a, i) => (
                <FadeIn key={a.label} delay={i * 0.06} className="group relative aspect-square border-r border-b border-line overflow-hidden">
                    <img src={a.img} alt={a.label} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-[transform,filter] duration-700" />
                    <div className="absolute inset-0 bg-black/55 group-hover:bg-black/30 transition-colors duration-500" />
                    <span className="absolute bottom-4 left-4 right-4 font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-white" data-testid={`application-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        {a.label}
                    </span>
                </FadeIn>
            ))}
        </div>
    </section>
);

const BigCta = () => (
    <section className="relative border-b border-line overflow-hidden" data-testid="home-big-cta">
        <div className="px-8 md:px-16 lg:px-24 py-32 md:py-44">
            <FadeIn><MonoLabel>05 / Get Started</MonoLabel></FadeIn>
            <h2 className="mt-8 font-heading font-black uppercase tracking-tighter leading-[0.9] text-[11vw] md:text-[7.5vw]" data-testid="big-cta-headline">
                <MaskedLine inView>Have a Ground</MaskedLine>
                <MaskedLine inView delay={0.1}>Engineering <span className="text-[#FF5C00]">Challenge?</span></MaskedLine>
            </h2>
            <FadeIn delay={0.25} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-8">
                <p className="text-white/70 text-base md:text-lg max-w-md">Talk to our team about the right product for your project.</p>
                <Link
                    to="/contact"
                    data-testid="big-cta-contact-button"
                    className="group inline-flex items-center justify-between gap-6 bg-[#FF5C00] text-white px-10 h-16 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300 shrink-0"
                >
                    Contact Solid Ground Solutions
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </FadeIn>
        </div>
    </section>
);

const Home = () => (
    <div data-testid="home-page">
        <Hero />
        <EditorialMarquee items={["Engineered Ground Solutions", "Geotextiles", "Geogrids", "Solid Ground"]} />
        <Intro />
        <Features />
        <Categories />
        <Applications />
        <BigCta />
    </div>
);

export default Home;
